import type { Html, HtmlBuilder } from "foldkit/html";
import * as lucide from "lucide";

type IconNode = typeof lucide.Clock;

export type IconOptions = Readonly<{
	/** Pixel size; lucide-react's default is 24. */
	size?: number;
	class?: string;
}>;

const kebab = (name: string): string =>
	name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

/** Renders a vanilla `lucide` icon node (`["svg", attrs, children]`) with lucide-react's default SVG attributes. */
export const icon = <M>(
	h: HtmlBuilder<M>,
	name: string,
	iconNode: IconNode,
	{ size = 24, class: className = "" }: IconOptions = {},
): Html =>
	h.svg(
		[
			h.Attribute("xmlns", "http://www.w3.org/2000/svg"),
			h.Width(String(size)),
			h.Height(String(size)),
			h.ViewBox("0 0 24 24"),
			h.Fill("none"),
			h.Stroke("currentColor"),
			h.StrokeWidth("2"),
			h.StrokeLinecap("round"),
			h.StrokeLinejoin("round"),
			h.Class(`lucide lucide-${kebab(name)} ${className}`.trim()),
			h.AriaHidden(true),
		],
		(iconNode[2] ?? []).map(([tag, attrs]) =>
			h[tag as "path"](
				Object.entries(attrs).map(([key, value]) =>
					h.Attribute(key, String(value)),
				),
			),
		),
	);

const make =
	(name: keyof typeof lucide) =>
	<M>(h: HtmlBuilder<M>, options?: IconOptions): Html =>
		icon(h, name, lucide[name] as IconNode, options);

// Every icon v1 imports from lucide-react.
export const AlertCircle = make("AlertCircle");
export const Biohazard = make("Biohazard");
export const Check = make("Check");
export const ChevronDown = make("ChevronDown");
export const ChevronLeft = make("ChevronLeft");
export const ChevronRight = make("ChevronRight");
export const CirclePlus = make("CirclePlus");
export const Clock = make("Clock");
export const CreditCard = make("CreditCard");
export const Download = make("Download");
export const FileText = make("FileText");
export const Hash = make("Hash");
export const Inbox = make("Inbox");
export const Link = make("Link");
export const Link2 = make("Link2");
export const Linkedin = make("Linkedin");
export const List = make("List");
export const Loader2 = make("Loader2");
export const Menu = make("Menu");
export const MessageSquare = make("MessageSquare");
export const Pencil = make("Pencil");
export const Plus = make("Plus");
export const RefreshCcw = make("RefreshCcw");
export const Search = make("Search");
export const Settings = make("Settings");
export const Sparkles = make("Sparkles");
export const Trash2 = make("Trash2");
export const TriangleAlert = make("TriangleAlert");
export const User = make("User");
export const X = make("X");
export const ArrowLeft = make("ArrowLeft");
export const Bold = make("Bold");
export const Bot = make("Bot");
export const Calendar = make("Calendar");
export const CheckSquare = make("CheckSquare");
export const CircleHelp = make("CircleHelp");
export const Code = make("Code");
export const FileCode = make("FileCode");
export const FileDown = make("FileDown");
export const FlaskConical = make("FlaskConical");
export const Heading1 = make("Heading1");
export const Heading2 = make("Heading2");
export const Heading3 = make("Heading3");
export const Image = make("Image");
export const Italic = make("Italic");
export const Lightbulb = make("Lightbulb");
export const ListOrdered = make("ListOrdered");
export const Paperclip = make("Paperclip");
export const Quote = make("Quote");
export const RefreshCw = make("RefreshCw");
export const Scale = make("Scale");
export const Send = make("Send");
export const StickyNote = make("StickyNote");
export const Strikethrough = make("Strikethrough");
export const Trophy = make("Trophy");
export const Workflow = make("Workflow");
